import posthog from 'posthog-js'
import { ApiAgent } from 'sprites-types/api/agents'
import { reporter } from './reporter'

/**
 * Analytics service for tracking events with PostHog
 * This service centralizes all event tracking and ensures consistent tracking across the app
 */
export const analytics = {
  /**
   * Identify a user with PostHog
   * @param userId - The user's ID
   * @param isAnonymous - Whether the user is anonymous
   * @param properties - Additional user properties
   */
  identify: (userId: string, isAnonymous: boolean, properties: Record<string, any> = {}) => {
    // Use posthog.identify to identify the user
    posthog.identify(userId, {
      is_anonymous: isAnonymous,
      ...properties,
    })

    // Set user properties using user.set to ensure they're persisted
    posthog.people.set({
      is_anonymous: isAnonymous,
      ...properties,
    })
  },

  /**
   * Reset the user identification
   */
  reset: () => {
    posthog.reset()
  },

  /**
   * Track report issue events
   */
  reportIssue: {
    reportIssue: (scope: string, rating?: number, agent?: ApiAgent, feedback?: string) => {
      const agentProperties = agent
        ? {
            agent_id: agent.id,
            agent_app_url: agent.appUrl,
            agent_description: agent.description,
            agent_is_active: agent.isActive,
            agent_name: agent.name,
            agent_slug_url: agent.slugUrl,
            agent_created_at: agent.createdAt,
            agent_updated_at: agent.updatedAt,
          }
        : {}

      const payload = {
        scope,
        rating,
        feedback,
        ...agentProperties,
      }
      posthog.capture('REPORT_ISSUE', payload)
    },
  },

  /**
   * Track a landing page event
   */
  landingPage: {
    templateClicked: (templateId: string, templateName: string) => {
      posthog.capture('TEMPLATE_CLICKED', {
        template_id: templateId,
        template_name: templateName,
      })
    },
    promptboxTabPressed: () => {
      posthog.capture('PROMPTBOX_TAB_PRESSED')
    },
    promptboxOptionClicked: (optionText: string) => {
      posthog.capture('PROMPTBOX_OPTION_CLICKED', {
        option_text: optionText,
      })
    },
    promptboxSubmitted: (inputText: string) => {
      posthog.capture('PROMPTBOX_SUBMITTED', {
        input_text: inputText,
      })
    },
  },

  /**
   * Track user lifecycle events
   */
  userLifecycle: {
    loginClicked: () => {
      posthog.capture('LOGIN_CLICKED')
    },
    loginMethodClicked: (method: string) => {
      posthog.capture('LOGIN_METHOD_CLICKED', {
        auth_method: method,
      })
    },
    loginSuccess: (method: string) => {
      posthog.capture('LOGIN_SUCCESS', {
        auth_method: method,
      })
    },
    signupComplete: (method: string) => {
      posthog.capture('SIGNUP_COMPLETE', {
        auth_method: method,
      })
    },
    loginError: (error: string, method: string) => {
      posthog.capture('LOGIN_ERROR', {
        error,
        auth_method: method,
      })
    },
    profileClicked: () => {
      posthog.capture('PROFILE_CLICKED')
    },
    billingClicked: () => {
      posthog.capture('BILLING_CLICKED')
    },
    logout: () => {
      posthog.capture('LOGOUT')
    },
  },

  /**
   * Track app creation and editing lifecycle events
   */
  appLifecycle: {
    appCreated: (agentId: string, workspaceId: string, appName: string) => {
      posthog.capture('APP_CREATED', {
        agent_id: agentId,
        workspace_id: workspaceId,
        app_name: appName,
      })
    },
    appForked: (agentId: string, workspaceId: string, sourceAgentId: string, appName: string) => {
      posthog.capture('APP_FORKED', {
        agent_id: agentId,
        workspace_id: workspaceId,
        source_agent_id: sourceAgentId,
        app_name: appName,
      })
    },
    appDeleted: (agentId: string, appName: string) => {
      posthog.capture('APP_DELETED', {
        agent_id: agentId,
        app_name: appName,
      })
    },
    appDeployed: (agentId: string, appUrl: string, appName: string) => {
      posthog.capture('APP_DEPLOYED', {
        agent_id: agentId,
        app_url: appUrl,
        app_name: appName,
      })
    },
    appEdited: (agentId: string, appName: string) => {
      posthog.capture('APP_EDITED', {
        agent_id: agentId,
        app_name: appName,
      })
    },
    appLinkCopied: (agentId: string) => {
      posthog.capture('APP_LINK_COPIED', {
        agent_id: agentId,
      })
    },
    appOpenedNewTab: (agentId: string, appUrl: string) => {
      posthog.capture('APP_OPENED_NEW_TAB', {
        agent_id: agentId,
        app_url: appUrl,
      })
    },
    appSettingsOpened: (agentId: string) => {
      posthog.capture('APP_SETTINGS_OPENED', {
        agent_id: agentId,
      })
    },
    appAppearanceOpened: (agentId: string) => {
      posthog.capture('APP_APPEARANCE_OPENED', {
        agent_id: agentId,
      })
    },
    appStepsToggled: (agentId: string, isOpen: boolean) => {
      posthog.capture('APP_STEPS_TOGGLED', {
        agent_id: agentId,
        is_open: isOpen,
      })
    },
    appStepsStepSelected: (agentId: string, stepId: string, hasPrompt: boolean) => {
      posthog.capture('APP_STEPS_STEP_SELECTED', {
        agent_id: agentId,
        step_id: stepId,
        has_prompt: hasPrompt,
      })
    },
    appPromptsSaved: (agentId: string, promptId: string) => {
      posthog.capture('APP_PROMPTS_SAVED', {
        agent_id: agentId,
        prompt_id: promptId,
      })
    },
    appPromptsCanceled: (agentId: string, promptId: string) => {
      posthog.capture('APP_PROMPTS_CANCELED', {
        agent_id: agentId,
        prompt_id: promptId,
      })
    },
  },

  /**
   * Track app player events
   */
  appPlayer: {
    appPlayerViewed: (agentId: string) => {
      posthog.capture('APP_PLAYER_VIEWED', {
        agent_id: agentId,
      })
    },
    appPlayerLoaded: (agentId: string) => {
      posthog.capture('APP_PLAYER_LOADED', {
        agent_id: agentId,
      })
    },
    appPlayerError: (agentId: string, error: string) => {
      posthog.capture('APP_PLAYER_ERROR', {
        agent_id: agentId,
        error,
      })
    },
    appPlayerWatermarkClicked: (agentId: string) => {
      posthog.capture('APP_PLAYER_WATERMARK_CLICKED', {
        agent_id: agentId,
      })
    },
    appPlayerExecuteClick: (agentId: string) => {
      posthog.capture('APP_PLAYER_EXECUTE_CLICK', {
        agent_id: agentId,
      })
    },
    appPlayerExecuteSuccess: (agentId: string) => {
      posthog.capture('APP_PLAYER_EXECUTE_SUCCESS', {
        agent_id: agentId,
      })
    },
    appPlayerExecuteFail: (agentId: string, error: string) => {
      posthog.capture('APP_PLAYER_EXECUTE_FAIL', {
        agent_id: agentId,
        error,
      })
    },
    appPlayerUserDragsKeyart: (agentId: string) => {
      posthog.capture('APP_PLAYER_USER_DRAGS_KEYART', {
        agent_id: agentId,
      })
    },
  },

  /**
   * Track chat/copilot events
   */
  copilot: {
    copilotConnected: (agentId: string, connectionId: string) => {
      posthog.capture('COPILOT_CONNECTED', {
        agent_id: agentId,
        connection_id: connectionId,
      })
    },
    copilotMessageSent: (properties: Record<string, string | null | undefined>) => {
      posthog.capture('COPILOT_MESSAGE_SENT', {
        ...properties,
        codegen_seconds: 0,
      })
    },
    copilotUnhandledError: (properties: Record<string, string | null | undefined>) => {
      posthog.capture('COPILOT_UNHANDLED_ERROR', properties)
    },
    copilotDisconnected: (properties: Record<string, string | null | undefined>) => {
      posthog.capture('COPILOT_DISCONNECTED', properties)
    },
    // Copilot actual app level 'message' received event
    copilotMessageReceived: (agentId: string, messageLength: number, messageContent: string) => {
      posthog.capture('COPILOT_MESSAGE_RECEIVED', {
        agent_id: agentId,
        message_length: messageLength,
        message_content: messageContent,
      })
    },
    copilotOnMessageReceived: (properties: Record<string, string | null | undefined>) => {
      posthog.capture('COPILOT_ON_MESSAGE_RECEIVED', properties)
    },
    copilotCustomizationMetadataUpdate: (agentId: string, metadataType: string) => {
      posthog.capture('COPILOT_CUSTOMIZATION_METADATA_UPDATE', {
        agent_id: agentId,
        metadata_type: metadataType,
      })
    },
    copilotLogoUpdate: (agentId: string) => {
      posthog.capture('COPILOT_LOGO_UPDATE', {
        agent_id: agentId,
      })
    },
    copilotKeyartUpdate: (agentId: string, keyartIndex: number) => {
      posthog.capture('COPILOT_KEYART_UPDATE', {
        agent_id: agentId,
        keyart_index: keyartIndex,
      })
    },
    copilotCustomizationMinimized: (agentId: string) => {
      posthog.capture('COPILOT_CUSTOMIZATION_MINIMIZED', {
        agent_id: agentId,
      })
    },
    copilotCustomizationExpand: (agentId: string) => {
      posthog.capture('COPILOT_CUSTOMIZATION_EXPAND', {
        agent_id: agentId,
      })
    },
  },

  /**
   * Track error events
   */
  errors: {
    somethingWentWrong: (error: string) => {
      posthog.capture('SOMETHING_WENT_WRONG', {
        error,
      })
    },
    disconnectToast: () => {
      posthog.capture('DISCONNECT_TOAST')
    },
    genericError: (error: string, context?: Record<string, any>) => {
      posthog.capture('GENERIC_ERROR', {
        error,
        ...context,
      })
    },
  },

  /**
   * Capture UTM parameters from URL
   * This should be called on page load to capture UTM parameters
   */
  captureUtmParameters: () => {
    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)
    const utmParams: Record<string, string> = {}

    // Extract all UTM parameters
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        utmParams[key] = value
      }
    })

    // If UTM parameters exist, set them as properties on the user
    if (Object.keys(utmParams).length > 0) {
      posthog.people.set(utmParams)

      // Also capture them as an event for immediate tracking
      posthog.capture('UTM_PARAMETERS_CAPTURED', utmParams)
    }
  },
}

// Export the reporter for backward compatibility
export { reporter }
